import { hasPermission } from '@/hooks/useBackendMenu'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import { createContext, useContext, useMemo } from 'react'
import {
    type NotaAdesiva,
    type NotasAdesivasQuery,
    notasAdesivas,
} from 'service/api'
import type { ViewMode } from '../../toggle-view'
import { QUERY_LIST_VIEW } from '../../utils/helpers'
import { type AncoraNota, agruparPorAncora, montarChaveAncora } from './ancora'
import { NotasAdesivasPopover } from './notas-adesivas-popover'

/** Notas da tela agrupadas por âncora; `null` quando o usuário não pode ler notas. */
export const NotasAdesivasContext = createContext<Map<
    string,
    NotaAdesiva[]
> | null>(null)

const SEM_NOTAS: NotaAdesiva[] = []

/**
 * Busca as notas do mesmo recorte da jornada exibida e as agrupa por âncora.
 * Sem permissão de leitura não busca e retorna `null`.
 */
export function useNotasAdesivasDigitacao(
    view: ViewMode,
    queryParams: Record<string, any>,
    isJornadaHabilitada: boolean,
) {
    const canLerNotas = hasPermission(['read', 'NotasAdesivas'])
    const query: NotasAdesivasQuery =
        view === 'mensal'
            ? {
                  funcionarioId: queryParams.funcionarioId,
                  // Batidas viradas do dia vizinho aparecem nas pontas da grade mensal.
                  dataInicial: dayjs(queryParams.dataInicial)
                      .subtract(1, 'day')
                      .format('YYYY-MM-DD'),
                  dataFinal: dayjs(queryParams.dataFinal)
                      .add(1, 'day')
                      .format('YYYY-MM-DD'),
              }
            : // A API já alarga a visão diária em ±1 dia.
              { empresaId: queryParams.empresaId, dia: queryParams.dia }

    const { data } = notasAdesivas.useFetch({
        queryParams: query,
        enabled: canLerNotas && isJornadaHabilitada,
    })

    return useMemo(
        () => (canLerNotas ? agruparPorAncora(data ?? SEM_NOTAS) : null),
        [canLerNotas, data],
    )
}

function useNotasDaAncora(ancora: AncoraNota) {
    const notasPorAncora = useContext(NotasAdesivasContext)
    if (!notasPorAncora) return null
    return notasPorAncora.get(montarChaveAncora(ancora)) ?? SEM_NOTAS
}

/** Formata o dia como no cabeçalho da coluna "Dia", ex.: "SEG 03/08". */
export function formatarDia(dia: string) {
    return dayjs(dia.slice(0, 10))
        .locale('pt-br')
        .format('ddd DD/MM')
        .toUpperCase()
}

interface NotasAdesivasDiaProps {
    funcionarioId: number
    /** `YYYY-MM-DD`. */
    dia: string
    /** Acrescentado ao título, ex.: o nome do funcionário na visão diária. */
    complemento?: string
    className?: string
}

/** Notas do dia inteiro de um funcionário; não renderiza sem permissão de leitura. */
export function NotasAdesivasDia({
    funcionarioId,
    dia,
    complemento,
    className,
}: NotasAdesivasDiaProps) {
    const ancora = { funcionarioId, data: dia }
    const notas = useNotasDaAncora(ancora)
    // No card não há `tr` cujo hover revele o ícone.
    const isListView = useMediaQuery(QUERY_LIST_VIEW)
    if (!notas) return null

    const titulo = [formatarDia(dia), complemento].filter(Boolean).join(' · ')

    return (
        <NotasAdesivasPopover
            titulo={titulo}
            ancora={ancora}
            notas={notas}
            className={className}
            variant={isListView ? 'static' : 'dynamic'}
        />
    )
}

interface NotasAdesivasRegistroProps {
    registroId: number
    /** `YYYY-MM-DD`. */
    dia: string
    /** Hora do registro no slot, quando houver. */
    hora?: string
    className?: string
}

/** Notas do registro do slot; não renderiza sem permissão de leitura. */
export function NotasAdesivasRegistro({
    registroId,
    dia,
    hora,
    className,
}: NotasAdesivasRegistroProps) {
    const notas = useNotasDaAncora({ registroId })
    if (!notas) return null

    return (
        <NotasAdesivasPopover
            titulo={`${formatarDia(dia)} | ${hora}`}
            ancora={{ registroId }}
            notas={notas}
            className={className}
        />
    )
}
